/// <reference path="../../../../../../../../node_modules/@flowx/monaco-editor/monaco.d.ts" />
import { CodeEditorFlxLanguage } from '@flowx/designer/modules/monaco-code-editor/types'
import {
  ReusableFunctionLanguageToCodeEditorLanguageMap,
  ReusableFunctionSummaryWithManifestInfo,
} from '@flowx/designer/modules/reusable-resources/reusable-functions/models/reusable-function.entity'
import { ManifestResourceType } from '@flowx/designer/shared/entities/manifest-resource-type.entity'
import { resourceRoute } from '@flowx/designer/shared/services/resource-navigation.service'

/**
 * A `functions.<app>.<name>(...)` reference resolved to the reusable function it
 * names — everything needed to open that function's own definition page.
 */
export type FunctionRefTarget = {
  /** The `functions.` path as written in the script, e.g. `core.getTax`. */
  fullName: string
  appName: string
  appId: string
  appVersionId: string
  resourceId: string
  /** The language the target function is written in. */
  language: CodeEditorFlxLanguage
}

export const FUNCTION_NAV_URI_SCHEME = 'flx-fn'

/**
 * Ends the URI of a definition's preview model.
 *
 * Monaco tags the code block it builds for the cmd/ctrl-hover preview with the
 * language it guesses from that URI, and an unguessable one falls back to the
 * language of the editor being hovered — which would syntax-highlight a line of
 * prose as JavaScript. `.txt` is the extension plaintext is registered under,
 * so the guess lands there and the preview renders unhighlighted, the same as
 * the hover below.
 */
export const FUNCTION_NAV_URI_SUFFIX = '.txt'

/** The language a code block must be tagged with to render unhighlighted. */
export const PLAINTEXT_LANGUAGE_ID = 'plaintext'

/**
 * Underlines a resolved reference so it reads as navigable at rest —
 * go-to-definition itself shows nothing until cmd/ctrl is held.
 */
export const FUNCTION_REFERENCE_DECORATION_CLASS = 'flx-function-reference'

/**
 * The only two languages a reusable function can be written in, and so the only
 * two a reference can resolve in. The runtime injects the `functions` namespace
 * filtered by language (`GraalFunctionsInjector`, `NativeFunctionsMap`), so a
 * reference across languages is never honoured and is never linked here.
 */
export const NAVIGABLE_SCRIPT_LANGUAGES = [
  CodeEditorFlxLanguage.Javascript,
  CodeEditorFlxLanguage.Python,
] as const

export type NavigableScriptLanguage = (typeof NAVIGABLE_SCRIPT_LANGUAGES)[number]

export type FunctionTargetIndex = Map<NavigableScriptLanguage, Map<string, FunctionRefTarget>>

export const isNavigableScriptLanguage = (
  language: string | null | undefined
): language is NavigableScriptLanguage =>
  NAVIGABLE_SCRIPT_LANGUAGES.includes(language as NavigableScriptLanguage)

export const LANGUAGE_LABELS: Record<NavigableScriptLanguage, string> = {
  [CodeEditorFlxLanguage.Javascript]: 'JavaScript',
  [CodeEditorFlxLanguage.Python]: 'Python',
}

/**
 * Every referenceable function, indexed by script language and then by the
 * `<app>.<name>` path it is written as. A function with no language recorded
 * lands under both, mirroring `ReusableFunctionConverter`, which falls back to
 * the calling script's language for exactly that case.
 */
export const buildTargetIndex = (
  functions: Record<string, ReusableFunctionSummaryWithManifestInfo>
): FunctionTargetIndex =>
  new Map(
    NAVIGABLE_SCRIPT_LANGUAGES.map((language) => [
      language,
      new Map(
        Object.entries(functions)
          .filter(([, fn]) => {
            const fnLanguage = ReusableFunctionLanguageToCodeEditorLanguageMap[fn.language]
            return !!fn.manifestInfo && (!fnLanguage || fnLanguage === language)
          })
          .map(([fullName, fn]) => [
            fullName,
            {
              fullName,
              appName: fn.manifestInfo.appName,
              appId: fn.manifestInfo.appId,
              appVersionId: fn.manifestInfo.appVersionId,
              resourceId: fn.resourceId,
              language,
            } satisfies FunctionRefTarget,
          ])
      ),
    ])
  )

/**
 * Router commands for the definition page of `target` — where every reference
 * leads. Built from the route table the rest of the designer opens resources
 * with, so a cross-application reference lands in the application it belongs
 * to; the open application's ids are only the fallback for a target that
 * records none.
 *
 * One segment per command, so `Router.createUrlTree` can be handed a base href
 * in front of them: it only splits its first command on `/`, and would encode
 * the slashes in any later one. `null` when the ids the route needs are missing.
 */
export const functionDefinitionCommands = (
  target: FunctionRefTarget,
  openApp: { workspaceId: string; appId: string; appVersionId: string }
): string[] | null =>
  resourceRoute(ManifestResourceType.ReusableFunction, {
    workspaceId: openApp.workspaceId,
    appId: target.appId || openApp.appId,
    appVersionId: target.appVersionId || openApp.appVersionId,
    resourceId: target.resourceId,
  })?.commands ?? null

/**
 * The address of a target: the URI of the model a definition points at.
 *
 * Paired with `parseTargetUri` — they are the two halves of one format, so a
 * change to either belongs in both.
 */
export const targetUriFor = (language: string, fullName: string): monaco.Uri =>
  monaco.Uri.from({
    scheme: FUNCTION_NAV_URI_SCHEME,
    // The language is part of the address: the same name can exist in both
    // languages, and a click must land on the one that was resolved. The suffix
    // is what Monaco reads the preview's own language off — see
    // FUNCTION_NAV_URI_SUFFIX.
    path: `/${language}/${encodeURIComponent(fullName)}${FUNCTION_NAV_URI_SUFFIX}`,
  })

/** Reads back what `targetUriFor` wrote, or null for any other URI. */
export const parseTargetUri = (uri: monaco.Uri): { language: string; fullName: string } | null => {
  if (uri.scheme !== FUNCTION_NAV_URI_SCHEME) {
    return null
  }

  const [language, fileName] = uri.path.replace(/^\//, '').split('/')
  if (!language || !fileName?.endsWith(FUNCTION_NAV_URI_SUFFIX)) {
    return null
  }

  return {
    language,
    fullName: decodeURIComponent(fileName.slice(0, -FUNCTION_NAV_URI_SUFFIX.length)),
  }
}

/**
 * What a maker sees when they hold cmd over a reference, and in the peek widget.
 *
 * One line, and it names the function rather than standing in for its body: the
 * hover already carries Monaco's own signature for the reference above it, and
 * the code itself is one click away. A single line also keeps clear of Monaco
 * stripping shared indentation off the preview, which a blank line breaks.
 */
export const functionPreviewContent = (target: FunctionRefTarget): string =>
  `${target.fullName} — ${LANGUAGE_LABELS[target.language]} reusable function in "${target.appName}"`

/**
 * The same line as a hover body. A plaintext code block, because that is exactly
 * what Monaco builds for its own definition preview — so the hover a maker gets
 * with the modifier held and the one they get without it read identically.
 */
export const functionPreviewMarkdown = (target: FunctionRefTarget): string =>
  [`\`\`\`${PLAINTEXT_LANGUAGE_ID}`, functionPreviewContent(target), '```'].join('\n')
